import React, { Fragment, ReactElement, useMemo } from "react";
import { useQuery } from "urql";
import { sortBy } from "lodash";
import { Link } from "wouter";

import { PARTIES_QUERY } from "../../../graphql/src/party/queries";
import { Party, PartyFilterInput } from "../types.generated";

import { ReactTable } from "../../../components/src/react-table/ReactTable";

import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../components/src/react-table/features";
import { SelectColumnFilterArrayOfStrings, DefaultColumnFilter } from "../../../components/src/react-table/ReactTableFilters";

export const PartiesPage = (): ReactElement => {
    const partiesFilter: PartyFilterInput = {};
    const [{ fetching: loading, error, data }] = useQuery<{ parties?: Partial<Party>[] }>({
        query: PARTIES_QUERY,
        variables: { filter: partiesFilter }
    });

    const parties = useMemo(() => {
        if (data && data.parties) {
            return sortBy(data.parties, "types", "name");
        }
        return [];
    }, [data]);

    const columns = useMemo<ReactTableColumnDef<Party>[]>(() => {
        const columnHelper = createReactTableColumnHelper<Party>();

        const columns = [
            columnHelper.accessor("_id", {
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <Link to={`/parties/${value}`}>{value}</Link>;
                },
                header: "Id",
                size: 200
            }),
            columnHelper.accessor("name", {
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    const { row } = cellProps;
                    return <Link to={`/parties/${row.original._id}`}>{value}</Link>;
                },
                header: "Name",
                size: 200,
                filter: DefaultColumnFilter
            }),
            columnHelper.accessor("types", {
                cell: (cellProps) => {
                    const types = cellProps.getValue();
                    return <div>{types.sort().join(", ")}</div>;
                },
                header: "Types",
                size: 200,
                filter: SelectColumnFilterArrayOfStrings
            }),
            columnHelper.accessor("legalEntityIdentifier", {
                header: "Legal entity identifier",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                },
                size: 150,
                filter: DefaultColumnFilter
            }),
            columnHelper.accessor("legalNumber", {
                header: "Legal number",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                },
                size: 75,
                filter: DefaultColumnFilter
            }),
            columnHelper.accessor("updateTimestamp", {
                header: "Update timestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                },
                size: 150
            })
        ];

        return columns;
    }, []);

    if (loading) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    return (
        <div>
            <div className="flex">
                <div className="ml-auto p-2">
                    <Link to={"/parties/new"}>New</Link>
                </div>
            </div>
            <Fragment>
                <p>Number of parties: {parties.length}</p>
                <ReactTable columns={columns} data={parties} />
            </Fragment>
        </div>
    );
};
